import { QueryClientProvider } from '@tanstack/react-query';
import { Tooltip } from 'radix-ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router';

import { AppShell } from '@/components/layout/app-shell';
import { ChurchSwitchingDialog } from '@/components/layout/church-switching-dialog';
import { CookieBanner } from '@/components/layout/cookie-banner';
import { Toaster } from '@/components/ui/toast';
import { SessionIdleGuard } from '@/components/auth/session-idle-guard';
import { ChurchBootstrap } from '@/lib/church-bootstrap';
import {
  RequireAdminOrOwner,
  RequireChurch,
  RequireModuleAccessLayout,
  RequirePermission,
} from '@/lib/guards';
import { PERMISSIONS } from '@/lib/permissions';
import { FINANCIAL_REPORT_ACCESS_PERMISSIONS } from '@/lib/report-permissions';
import { queryClient } from '@/lib/query-client';
import { RequireAuth } from '@/lib/require-auth';
import { AuditLogPage } from '@/pages/audit/audit-log-page';
import { FinancialAuditPage } from '@/pages/audit/financial-audit-page';
import { SubscriptionsLegacyRedirect } from '@/pages/subscriptions/subscriptions-legacy-redirect';
import { SubscriptionsHistoryPage } from '@/pages/subscriptions/subscriptions-history-page';
import { SubscriptionsLayout } from '@/pages/subscriptions/subscriptions-layout';
import { SubscriptionsOverviewPage } from '@/pages/subscriptions/subscriptions-overview-page';
import { SubscriptionsPlansRedirect } from '@/pages/subscriptions/subscriptions-plans-redirect';
import { SubscriptionsReferPage } from '@/pages/subscriptions/subscriptions-refer-page';
import { AccountDeletePage } from '@/pages/account/account-delete-page';
import { AccountLayout } from '@/pages/account/account-layout';
import { AccountPage } from '@/pages/account/account-page';
import { StaffRolesPage } from '@/pages/admin/staff-roles-page';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
import { AcceptInvitePage } from '@/pages/auth/accept-invite';
import { RegisterPage } from '@/pages/auth/register';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { SignInPage } from '@/pages/auth/sign-in';
import { SupportAccessPage } from '@/pages/support-access-page';
import { SupportTicketDetailPage } from '@/pages/support/support-ticket-detail-page';
import { SupportTicketsPage } from '@/pages/support/support-tickets-page';
import { VerifyEmailPage } from '@/pages/auth/verify-email';
import { BranchesPage } from '@/pages/branches/branches-page';
import { ChurchHomePage } from '@/pages/churches/church-home-page';
import { AboutPage } from '@/pages/landing/about-page';
import { ContactPage } from '@/pages/landing/contact-page';
import { DemoPage } from '@/pages/landing/demo-page';
import { DeleteMePage } from '@/pages/landing/delete-me-page';
import { LandingPage } from '@/pages/landing/landing-page';
import { FeaturesPage } from '@/pages/landing/features-page';
import { FreePage } from '@/pages/landing/free-page';
import { PricingPage } from '@/pages/landing/pricing-page';
import { CookiePolicyPage } from '@/pages/legal/cookie-policy-page';
import { PrivacyPolicyPage } from '@/pages/legal/privacy-policy-page';
import { TermsPage } from '@/pages/legal/terms-page';
import { CombinedReportsPage } from '@/pages/reports/combined-reports-page';
import { ReportFavoritesPage } from '@/pages/reports/report-favorites-page';
import { ReportsPage } from '@/pages/reports/reports-page';
import { CustomReportDetailPage } from '@/pages/reports/custom-report-detail-page';
import { CustomReportFormPage } from '@/pages/reports/custom-report-form-page';
import { CustomReportsListPage } from '@/pages/reports/custom-reports-list-page';
import { ChurchSettingsPage } from '@/pages/churches/church-settings-page';
import { CreateChurchPage } from '@/pages/churches/create-church-page';
import { SelectChurchPage } from '@/pages/churches/select-church-page';
import { WidgetGalleryPage } from '@/pages/gallery/widget-gallery';
import { MessagingPage } from '@/pages/messaging/messaging-page';
import { ModulesPage } from '@/pages/modules/modules-page';
import { CommitteesPage } from '@/pages/groups/committees-page';
import { ManualGroupsPage } from '@/pages/groups/manual-groups-page';
import { GenerationalGroupsPage } from '@/pages/groups/generational-groups-page';
import { GroupDetailPage } from '@/pages/groups/group-detail-page';
import { CouncilPage } from '@/pages/people/council-page';
import { GroupLeadershipPage } from '@/pages/people/group-leadership-page';
import { DepartmentDetailPage } from '@/pages/people/department-detail-page';
import { DepartmentsPage } from '@/pages/people/departments-page';
import { ContributionDetailPage } from '@/pages/giving/contribution-detail-page';
import { ContributionsPage } from '@/pages/giving/contributions-page';
import { FundDetailPage } from '@/pages/giving/fund-detail-page';
import { FundTypesPage } from '@/pages/giving/fund-types-page';
import { FundsPage } from '@/pages/giving/funds-page';
import { PeriodsPage } from '@/pages/giving/periods-page';
import { PledgesProjectsPage } from '@/pages/giving/pledges-projects-page';
import { ServiceOfferingsPage } from '@/pages/giving/service-offerings-page';
import { TithesPage } from '@/pages/giving/tithes-page';
import { ChurchCalendarPage } from '@/pages/programs/church-calendar-page';
import { ProgramDetailPage } from '@/pages/programs/program-detail-page';
import { ProgramsPage } from '@/pages/programs/programs-page';
import { ServiceDetailPage } from '@/pages/programs/service-detail-page';
import { ServicesPage } from '@/pages/programs/services-page';

import { SessionsPage } from '@/pages/attendance/sessions-page';
import { SessionDetailPage } from '@/pages/attendance/session-detail-page';
import { KiosksPage } from '@/pages/attendance/kiosks-page';
import { BudgetDetailPage } from '@/pages/spending/budget-detail-page';
import { BudgetsPage } from '@/pages/spending/budgets-page';
import { ExpensesPage } from '@/pages/spending/expenses-page';
import { WelfarePage } from '@/pages/spending/welfare-page';
import { FinancialPositionPage } from '@/pages/spending/financial-position-page';
import { HubtelPage } from '@/pages/hubtel/hubtel-page';
import { PaymentsPage } from '@/pages/payments/payments-page';
import { PrayerRequestDetailPage } from '@/pages/prayer/prayer-request-detail-page';
import { PrayerRequestsPage } from '@/pages/prayer/prayer-requests-page';
import { SoulsWonPage } from '@/pages/evangelism/souls-won-page';
import { SoulWonDetailPage } from '@/pages/evangelism/soul-won-detail-page';
import { CommunityPage } from '@/pages/community/community-page';
import { AssetsPage } from '@/pages/assets/assets-page';
import { RentalsPage } from '@/pages/assets/rentals-page';
import { DocumentsPage } from '@/pages/documents/documents-page';
import { AppointmentsPage } from '@/pages/pastoral/appointments-page';
import { CounselingPage } from '@/pages/pastoral/counseling-page';
import { VisitsPage } from '@/pages/pastoral/visits-page';
import { SacramentRegisterPage } from '@/pages/sacraments/sacrament-register-page';
import { AnniversariesPage } from '@/pages/people/anniversaries-page';
import { BirthdaysPage } from '@/pages/people/birthdays-page';
import { FamiliesPage } from '@/pages/people/families-page';
import { FollowUpsPage } from '@/pages/people/follow-ups-page';
import { JoinRequestsPage } from '@/pages/people/join-requests-page';
import { JoinRequestDuplicatesPage } from '@/pages/people/join-request-duplicates-page';
import { MemberDetailPage } from '@/pages/people/member-detail-page';
import { MemberDuplicatesPage } from '@/pages/people/member-duplicates-page';
import { MembersPage } from '@/pages/people/members-page';
import { MinistersPage } from '@/pages/people/ministers-page';
import { ContentHomePage } from '@/pages/content/content-home-page';
import { SermonsPage } from '@/pages/content/sermons-page';
import { DevotionalsPage } from '@/pages/content/devotionals-page';
import { AnnouncementsPage } from '@/pages/content/announcements-page';
import { VideosPage } from '@/pages/content/videos-page';
import { GalleriesPage } from '@/pages/content/galleries-page';
import { ChurchStoryPage } from '@/pages/content/church-story-page';
import { GalleryDetailPage } from '@/pages/content/gallery-detail-page';
import { ContentCommentsPage } from '@/pages/content/content-comments-page';
import { SocialsPage } from '@/pages/content/socials-page';
import GlobalSearchPage from '@/pages/search/global-search-page';
import { NotificationsPage } from '@/pages/notifications/notifications-page';
import { DevicesPage } from '@/pages/security/devices-page';
import { MigrationAssistantPage } from '@/pages/settings/migration-assistant-page';
import { HubtelSettingsPage } from '@/pages/settings/hubtel-settings-page';
import { OnlineGivingSettingsPage } from '@/pages/settings/online-giving-settings-page';
import { SecuritySettingsPage } from '@/pages/settings/security-page';
import { SettingsLayout } from '@/pages/settings/settings-layout';
import { VenuesPage } from '@/pages/settings/venues-page';
import { watchSystemTheme } from '@/stores/theme-store';

/**
 * The application shell.
 *
 * Auth screens sit outside tenancy. After sign-in, church create/select run before
 * the console shell; everything else is behind `RequireChurch`.
 */
export const App = () => {
  useEffect(() => watchSystemTheme(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <Tooltip.Provider delayDuration={300}>
        <SessionIdleGuard />
        <BrowserRouter>
          <ChurchSwitchingDialog />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/free" element={<FreePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/delete-me" element={<DeleteMePage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/support-access" element={<SupportAccessPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />

            <Route
              path="/account/delete"
              element={
                <RequireAuth>
                  <div className="mx-auto min-h-dvh max-w-6xl bg-surface-sunken px-4 py-8 sm:px-6">
                    <AccountDeletePage />
                  </div>
                </RequireAuth>
              }
            />

            <Route path="/gallery" element={<WidgetGalleryPage />} />

            <Route
              path="/churches/new"
              element={
                <RequireAuth>
                  <ChurchBootstrap>
                    <CreateChurchPage />
                  </ChurchBootstrap>
                </RequireAuth>
              }
            />
            <Route
              path="/churches/select"
              element={
                <RequireAuth>
                  <ChurchBootstrap>
                    <SelectChurchPage />
                  </ChurchBootstrap>
                </RequireAuth>
              }
            />

            <Route
              element={
                <RequireAuth>
                  <RequireChurch>
                    <AppShell />
                  </RequireChurch>
                </RequireAuth>
              }
            >
              <Route path="search" element={<GlobalSearchPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route
                path="subscriptions"
                element={
                  <RequireAdminOrOwner>
                    <SubscriptionsLayout />
                  </RequireAdminOrOwner>
                }
              >
                <Route index element={<SubscriptionsOverviewPage />} />
                <Route path="plans" element={<SubscriptionsPlansRedirect />} />
                <Route path="history" element={<SubscriptionsHistoryPage />} />
                <Route path="refer" element={<SubscriptionsReferPage />} />
              </Route>
              <Route path="account" element={<AccountLayout />}>
                <Route index element={<AccountPage />} />
                <Route path="devices" element={<DevicesPage />} />
                <Route path="security" element={<Navigate to="/settings/security" replace />} />
              </Route>
              <Route path="settings" element={<SettingsLayout />}>
                <Route
                  path="migration-assistant"
                  element={
                    <RequireAdminOrOwner>
                      <MigrationAssistantPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="church"
                  element={
                    <RequireAdminOrOwner>
                      <ChurchSettingsPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="venues"
                  element={
                    <RequireAdminOrOwner>
                      <VenuesPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="staff"
                  element={
                    <RequireAdminOrOwner>
                      <StaffRolesPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="modules"
                  element={
                    <RequireAdminOrOwner>
                      <ModulesPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route path="billing" element={<SubscriptionsLegacyRedirect />} />
                <Route
                  path="online-giving"
                  element={
                    <RequirePermission permission={PERMISSIONS.PAYMENTS_CONFIGURE}>
                      <OnlineGivingSettingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="hubtel"
                  element={
                    <RequireAdminOrOwner>
                      <HubtelSettingsPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="hubtel/credits"
                  element={
                    <RequireAdminOrOwner>
                      <HubtelSettingsPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="hubtel/connection"
                  element={
                    <RequireAdminOrOwner>
                      <HubtelSettingsPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="security"
                  element={
                    <RequireAdminOrOwner>
                      <SecuritySettingsPage />
                    </RequireAdminOrOwner>
                  }
                />
                <Route
                  path="audit"
                  element={<Navigate to="/audit" replace />}
                />
                <Route
                  path="audit/financial"
                  element={<Navigate to="/audit/financial" replace />}
                />
                <Route path="devices" element={<Navigate to="/account/devices" replace />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="reporting" />}>
                <Route path="home" element={<ChurchHomePage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/favorites" element={<ReportFavoritesPage />} />
                <Route path="reports/combined" element={<CombinedReportsPage />} />
                <Route path="reports/custom" element={<CustomReportsListPage />} />
                <Route path="reports/custom/new" element={<CustomReportFormPage />} />
                <Route path="reports/custom/:id" element={<CustomReportDetailPage />} />
                <Route path="reports/custom/:id/edit" element={<CustomReportFormPage />} />
                <Route path="reports/:reportKey" element={<LegacyReportDeepLinkRedirect />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="audit" />}>
                <Route path="audit" element={<AuditLogPage />} />
                <Route path="audit/financial" element={<FinancialAuditPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="people" />}>
                <Route path="people" element={<MembersPage />} />
                <Route path="people/families" element={<FamiliesPage />} />
                <Route path="people/birthdays" element={<BirthdaysPage />} />
                <Route path="people/anniversaries" element={<AnniversariesPage />} />
                <Route path="people/follow-ups" element={<FollowUpsPage />} />
                <Route
                  path="people/join-requests/duplicate-check"
                  element={<JoinRequestDuplicatesPage />}
                />
                <Route path="people/join-requests" element={<JoinRequestsPage />} />
                <Route path="people/duplicates" element={<MemberDuplicatesPage />} />
                <Route
                  path="people/leadership"
                  element={<Navigate to="/people/leadership/council" replace />}
                />
                <Route path="people/departments" element={<DepartmentsPage />} />
                <Route path="people/departments/:departmentId" element={<DepartmentDetailPage />} />
                <Route
                  path="people/leadership/departments"
                  element={<Navigate to="/people/departments" replace />}
                />
                <Route
                  path="people/leadership/departments/:departmentId"
                  element={<LegacyLeadershipDepartmentRedirect />}
                />
                <Route path="people/leadership/council" element={<CouncilPage />} />
                <Route path="people/leadership/ministers" element={<MinistersPage />} />
                <Route path="people/leadership/groups/:groupId" element={<GroupLeadershipPage />} />
                <Route
                  path="people/ministers"
                  element={<Navigate to="/people/leadership/ministers" replace />}
                />
                <Route path="people/:memberId" element={<MemberDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="groups" />}>
                <Route path="committees/:groupId" element={<GroupDetailPage />} />
                <Route path="committees" element={<CommitteesPage />} />
                <Route path="groups/auto-filled" element={<GenerationalGroupsPage />} />
                <Route path="groups/manual" element={<Navigate to="/groups" replace />} />
                <Route path="groups/committees" element={<Navigate to="/committees" replace />} />
                <Route path="groups/:groupId" element={<GroupDetailPage />} />
                <Route path="groups" element={<ManualGroupsPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="programs" />}>
                <Route path="calendar" element={<ChurchCalendarPage />} />
                <Route path="services" element={<ServicesPage />} />
                <Route path="services/:programId" element={<ServiceDetailPage />} />
                <Route path="programs" element={<ProgramsPage />} />
                <Route path="programs/:programId" element={<ProgramDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="attendance" />}>
                <Route path="attendance" element={<SessionsPage />} />
                <Route path="attendance/sessions/:sessionId" element={<SessionDetailPage />} />
                <Route path="attendance/kiosks" element={<KiosksPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="giving" />}>
                <Route path="giving" element={<ContributionsPage />} />
                <Route
                  path="giving/contributions/:contributionId"
                  element={<ContributionDetailPage />}
                />
                <Route path="giving/tithes" element={<TithesPage />} />
                <Route path="giving/services" element={<ServiceOfferingsPage />} />
                <Route path="giving/services/:occurrenceId" element={<ServiceOfferingsPage />} />
                <Route path="giving/funds" element={<FundsPage />} />
                <Route path="giving/funds/:fundId" element={<FundDetailPage />} />
                <Route path="giving/types" element={<FundTypesPage />} />
                <Route path="giving/periods" element={<PeriodsPage />} />
              </Route>
              <Route
                element={
                  <RequireModuleAccessLayout
                    module="projects"
                    permissions={[PERMISSIONS.PROJECTS_VIEW, PERMISSIONS.GIVING_VIEW]}
                  />
                }
              >
                <Route path="projects" element={<PledgesProjectsPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="spending" />}>
                <Route path="expenses" element={<ExpensesPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="welfare" />}>
                <Route path="welfare" element={<WelfarePage />} />
              </Route>
              <Route
                element={
                  <RequireModuleAccessLayout
                    module="spending"
                    permissions={FINANCIAL_REPORT_ACCESS_PERMISSIONS}
                  />
                }
              >
                <Route path="financial-position" element={<FinancialPositionPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="budgets" />}>
                <Route path="budgets" element={<BudgetsPage />} />
                <Route path="budgets/:budgetId" element={<BudgetDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="payments" />}>
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="hubtel" element={<HubtelPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="messaging" />}>
                <Route path="messaging" element={<MessagingPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="content" />}>
                <Route path="content" element={<ContentHomePage />} />
                <Route path="content/sermons" element={<SermonsPage />} />
                <Route path="content/devotionals" element={<DevotionalsPage />} />
                <Route path="content/announcements" element={<AnnouncementsPage />} />
                <Route path="content/videos" element={<VideosPage />} />
                <Route path="content/galleries" element={<GalleriesPage />} />
                <Route path="chronicle" element={<ChurchStoryPage />} />
                <Route path="content/comments" element={<ContentCommentsPage />} />
                <Route path="content/socials" element={<SocialsPage />} />
                <Route path="content/galleries/:galleryId" element={<GalleryDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="prayer" />}>
                <Route path="prayer" element={<PrayerRequestsPage />} />
                <Route path="prayer/:requestId" element={<PrayerRequestDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="evangelism" />}>
                <Route path="evangelism" element={<SoulsWonPage />} />
                <Route path="evangelism/:soulWonId" element={<SoulWonDetailPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="community" />}>
                <Route path="community" element={<CommunityPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="pastoral" />}>
                <Route path="pastoral" element={<Navigate to="/pastoral/visits" replace />} />
                <Route path="pastoral/visits" element={<VisitsPage />} />
                <Route path="pastoral/counseling" element={<CounselingPage />} />
                <Route path="pastoral/appointments" element={<AppointmentsPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="documents" />}>
                <Route path="documents" element={<DocumentsPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="sacraments" />}>
                <Route path="sacraments" element={<Navigate to="/sacraments/baptisms" replace />} />
                <Route path="sacraments/:registerKind" element={<SacramentRegisterPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="assets" />}>
                <Route path="assets" element={<AssetsPage />} />
                <Route path="assets/rentals" element={<RentalsPage />} />
              </Route>
              <Route element={<RequireModuleAccessLayout module="branches" />}>
                <Route path="branches" element={<BranchesPage />} />
              </Route>
              <Route
                path="support/tickets"
                element={
                  <RequirePermission permission={PERMISSIONS.SUPPORT_TICKETS_VIEW}>
                    <SupportTicketsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="support/tickets/:ticketId"
                element={
                  <RequirePermission permission={PERMISSIONS.SUPPORT_TICKETS_VIEW}>
                    <SupportTicketDetailPage />
                  </RequirePermission>
                }
              />
              <Route path="support" element={<Navigate to="/support/tickets" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>

        <ToasterWithLabels />
      </Tooltip.Provider>
    </QueryClientProvider>
  );
};

const LegacyLeadershipDepartmentRedirect = () => {
  const { departmentId } = useParams();
  return <Navigate to={`/people/departments/${departmentId}`} replace />;
};

const LegacyReportDeepLinkRedirect = () => {
  const { reportKey } = useParams();
  if (!reportKey) return <Navigate to="/reports" replace />;
  return <Navigate to={`/reports?report=${encodeURIComponent(reportKey)}`} replace />;
};

const ToasterWithLabels = () => {
  const { t } = useTranslation();
  return <Toaster closeLabel={t('action.close')} />;
};
